import { useEffect, useRef } from 'react';

/**
 * Cosmic 백그라운드 — 별 380 (twinkle, fade-in) + bright stars 8 (diffraction halo)
 * + nebula 5 (drift) + constellation 36 nodes (drift+breath, 2 amber hubs).
 *
 * `.cosmic-stage` 안에서 사용. CSS variables (--bg / --star / --neb-* / --fg / --accent)
 * 를 getComputedStyle 로 읽어 다크/라이트 자동 추종. theme prop 으로 강제 재시작.
 */
export function CosmicBackground({ theme }: { theme: 'dark' | 'light' }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // theme 변경 시 canvas 재초기화 (CSS variables 가 다시 평가되도록).
  // biome-ignore lint/correctness/useExhaustiveDependencies: theme 변경이 effect 의 trigger
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    interface Star {
      x: number;
      y: number;
      r: number;
      tw: number;
      spd: number;
      depth: number;
      born: number;
    }
    interface BrightStar {
      x: number;
      y: number;
      r: number;
      tw: number;
      spd: number;
      hue: number;
      born: number;
    }
    interface Nebula {
      x: number;
      y: number;
      r: number;
      which: number;
      drift: number;
      spd: number;
    }
    interface CNode {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      breath: number;
      nx: number;
      ny: number;
      born: number;
    }

    const stars: Star[] = [];
    for (let i = 0; i < 380; i++) {
      stars.push({
        x: Math.random(),
        y: Math.random(),
        r: Math.random() * 1.2 + 0.2,
        tw: Math.random() * Math.PI * 2,
        spd: 0.25 + Math.random() * 0.7,
        depth: 0.3 + Math.random() * 0.7,
        born: Math.random() * 6.0,
      });
    }
    const brightStars: BrightStar[] = [];
    for (let i = 0; i < 8; i++) {
      brightStars.push({
        x: Math.random(),
        y: Math.random(),
        r: 1.5 + Math.random() * 1.3,
        tw: Math.random() * Math.PI * 2,
        spd: 0.2 + Math.random() * 0.5,
        hue: 30 + Math.random() * 240,
        born: 1.5 + Math.random() * 5.0,
      });
    }
    const nebs: Nebula[] = [];
    for (let i = 0; i < 5; i++) {
      nebs.push({
        x: Math.random(),
        y: Math.random(),
        r: 0.4 + Math.random() * 0.5,
        which: i % 3,
        drift: Math.random() * Math.PI * 2,
        spd: 0.02 + Math.random() * 0.03,
      });
    }
    const cnodes: CNode[] = [];
    for (let i = 0; i < 36; i++) {
      cnodes.push({
        x: Math.random(),
        y: Math.random(),
        vx: (Math.random() - 0.5) * 0.00006,
        vy: (Math.random() - 0.5) * 0.00006,
        size: 1.0 + Math.random() * 1.4,
        breath: Math.random() * Math.PI * 2,
        nx: Math.random() * 1000,
        ny: Math.random() * 1000,
        born: 2.0 + Math.random() * 5.0,
      });
    }
    const hubs = [
      Math.floor(Math.random() * cnodes.length),
      Math.floor(Math.random() * cnodes.length),
    ];

    const startT = performance.now() / 1000;

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let raf = 0;
    const loop = () => {
      const now = performance.now() / 1000;
      const elapsed = now - startT;
      const t = now;
      const W = canvas.clientWidth;
      const H = canvas.clientHeight;
      // .cosmic-stage 의 CSS variables 를 직접 읽음 (다크/라이트 토큰 자동 반영)
      const stage = canvas.parentElement;
      const css = stage ? getComputedStyle(stage) : getComputedStyle(document.body);
      const bg = css.getPropertyValue('--bg').trim() || '#05060a';
      const starColor = css.getPropertyValue('--star').trim() || 'rgba(255,255,255,0.7)';
      const fgColor = css.getPropertyValue('--fg').trim() || '#e8ecf5';
      const lineCol = css.getPropertyValue('--line-2').trim() || 'rgba(255,255,255,0.14)';
      const isDark = stage?.getAttribute('data-theme') !== 'light';
      const nebColors = [
        css.getPropertyValue('--neb-a').trim() || 'oklch(0.5 0.18 295 / 0.22)',
        css.getPropertyValue('--neb-b').trim() || 'oklch(0.55 0.16 200 / 0.18)',
        css.getPropertyValue('--neb-c').trim() || 'oklch(0.6 0.18 50 / 0.16)',
      ];
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // nebulae
      ctx.save();
      ctx.globalCompositeOperation = isDark ? 'lighter' : 'multiply';
      for (const n of nebs) {
        const cx = (n.x + Math.cos(t * n.spd + n.drift) * 0.07) * W;
        const cy = (n.y + Math.sin(t * n.spd * 0.8 + n.drift) * 0.07) * H;
        const r = n.r * Math.max(W, H);
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, nebColors[n.which] ?? 'transparent');
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // stars: fade in over their birth time
      ctx.fillStyle = starColor;
      for (const s of stars) {
        const reveal = Math.min(1, Math.max(0, (elapsed - s.born) / 1.4));
        if (reveal <= 0) continue;
        const tw = 0.55 + 0.45 * Math.sin(t * s.spd + s.tw);
        ctx.globalAlpha = tw * s.depth * reveal;
        ctx.beginPath();
        ctx.arc(s.x * W, s.y * H, s.r * (0.6 + 0.4 * tw), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // bright stars with diffraction cross
      for (const s of brightStars) {
        const reveal = Math.min(1, Math.max(0, (elapsed - s.born) / 1.6));
        if (reveal <= 0) continue;
        const tw = (0.55 + 0.45 * Math.sin(t * s.spd + s.tw)) * reveal;
        const x = s.x * W;
        const y = s.y * H;
        const r = s.r * (0.7 + 0.3 * tw);
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 12);
        const haloCol = isDark
          ? `oklch(0.85 0.12 ${s.hue} / ${0.18 * tw})`
          : `oklch(0.5 0.14 ${s.hue} / ${0.12 * tw})`;
        grad.addColorStop(0, haloCol);
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, r * 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = haloCol;
        ctx.lineWidth = 0.7;
        ctx.globalAlpha = tw;
        ctx.beginPath();
        ctx.moveTo(x - r * 9, y);
        ctx.lineTo(x + r * 9, y);
        ctx.moveTo(x, y - r * 9);
        ctx.lineTo(x, y + r * 9);
        ctx.stroke();
        ctx.fillStyle = isDark ? '#ffffff' : '#1a1a1a';
        ctx.globalAlpha = 0.95 * tw;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // constellation: drift + breath
      for (const n of cnodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0.05 || n.x > 0.95) n.vx *= -1;
        if (n.y < 0.05 || n.y > 0.95) n.vy *= -1;
      }
      ctx.strokeStyle = lineCol;
      ctx.lineWidth = 0.6;
      const REACH = 200;
      for (let i = 0; i < cnodes.length; i++) {
        const a = cnodes[i];
        if (!a) continue;
        const aRev = Math.min(1, Math.max(0, (elapsed - a.born) / 1.6));
        if (aRev <= 0) continue;
        for (let j = i + 1; j < cnodes.length; j++) {
          const b = cnodes[j];
          if (!b) continue;
          const bRev = Math.min(1, Math.max(0, (elapsed - b.born) / 1.6));
          if (bRev <= 0) continue;
          const ax = a.x * W + Math.sin(t * 0.3 + a.nx) * 4;
          const ay = a.y * H + Math.cos(t * 0.27 + a.ny) * 4;
          const bx = b.x * W + Math.sin(t * 0.3 + b.nx) * 4;
          const by = b.y * H + Math.cos(t * 0.27 + b.ny) * 4;
          const dx = ax - bx;
          const dy = ay - by;
          const d = Math.hypot(dx, dy);
          if (d < REACH) {
            const a01 = 1 - d / REACH;
            ctx.globalAlpha = a01 * a01 * 0.45 * Math.min(aRev, bRev);
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      for (let i = 0; i < cnodes.length; i++) {
        const n = cnodes[i];
        if (!n) continue;
        const reveal = Math.min(1, Math.max(0, (elapsed - n.born) / 1.6));
        if (reveal <= 0) continue;
        const isHub = hubs.includes(i);
        const x = n.x * W + Math.sin(t * 0.3 + n.nx) * 4;
        const y = n.y * H + Math.cos(t * 0.27 + n.ny) * 4;
        const breathe = 0.7 + 0.3 * Math.sin(t * 0.6 + n.breath);
        const sz = (isHub ? n.size * 1.8 : n.size) * breathe;
        if (isHub) {
          const grad = ctx.createRadialGradient(x, y, 0, x, y, sz * 9);
          grad.addColorStop(
            0,
            isDark
              ? `oklch(0.78 0.14 50 / ${0.32 * breathe * reveal})`
              : `oklch(0.55 0.16 50 / ${0.22 * breathe * reveal})`,
          );
          grad.addColorStop(1, 'transparent');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(x, y, sz * 9, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = isHub ? (isDark ? 'oklch(0.85 0.16 50)' : 'oklch(0.55 0.17 50)') : fgColor;
        ctx.globalAlpha = (isHub ? 0.95 : 0.55) * reveal;
        ctx.beginPath();
        ctx.arc(x, y, sz, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // soft vignette
      const vig = ctx.createRadialGradient(
        W * 0.5,
        H * 0.5,
        Math.min(W, H) * 0.2,
        W * 0.5,
        H * 0.5,
        Math.max(W, H) * 0.75,
      );
      vig.addColorStop(0, 'transparent');
      vig.addColorStop(1, isDark ? 'rgba(0,0,0,0.4)' : 'rgba(245,241,232,0.3)');
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, W, H);

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [theme]);

  return <canvas ref={canvasRef} className="bg-canvas" tabIndex={-1} aria-label="장식 배경" />;
}
